import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllLedgerCategory, selectLedgerCategory } from '@store/masterData/ledgerCategorySlice';
import { getAllPositions, selectPosition } from '@store/masterData/positionSlice';
import { RootState } from 'src/redux/store';
import { LedgerClient, PayGlobalExportResponse, TripCrewWorkerLedgerResponse } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface Ledger extends TripCrewWorkerLedgerResponse {
    categoryDescription: string;
    categorySortOrder: number;
    workerPositionSortOrder: number;
}

export interface PayGlobalExportData extends PayGlobalExportResponse { }

export interface State {
    isLoading: boolean;
    isProcessing: boolean;
    error: boolean;
    postedLedgerList: Ledger[];
}

const initialState: State = {
    isLoading: false,
    isProcessing: false,
    error: false,
    postedLedgerList: [],
};

const ledgerSlice = createSlice({
    name: 'ledger',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        updateProcessing: (state, action: PayloadAction<boolean>) => {
            state.isProcessing = action.payload;
        },
        hasError: (state, action) => {
            state.isLoading = false;
            state.isProcessing = false;
            state.error = action.payload;
        },
        getPostedLedgerSuccess: (state, action: PayloadAction<Ledger[]>) => {
            state.postedLedgerList = action.payload;
            state.isLoading = false;
        },
        deletePostedLedgerAction: (state, action: PayloadAction<string>) => {
            const exportId = action.payload;
            state.postedLedgerList = state.postedLedgerList.filter(l => l.exportId !== exportId);
            state.isProcessing = false;
        },
    },
});

// Reducer
export default ledgerSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    updateProcessing,
    getPostedLedgerSuccess,
    deletePostedLedgerAction,
} = ledgerSlice.actions;

// for useSelector
export const selectLedger = (state: RootState) => state.ledger as State;

// ----------------------------------------------------------------------
export async function addFieldsInfo(dispatch: any, getState: any, response: TripCrewWorkerLedgerResponse[]) {
    await dispatch(getAllLedgerCategory());
    const { ledgerCategoryList } = selectLedgerCategory(getState());
    const ledgerCategoryMap = new Map(ledgerCategoryList.map(lc => [lc.ledgerCategoryCode, lc]));

    await dispatch(getAllPositions());
    const { positionList } = selectPosition(getState());
    const positionMap = new Map(positionList.map(p => [p.positionCode, p.sortOrder]));

    return response.map(r => {
        const ledgerCategory = ledgerCategoryMap.get(r.category);
        const workerPositionSortOrder = positionMap.get(r.workerPosition) ?? 0;
        return {
            ...r,
            categoryDescription: ledgerCategory?.ledgerCategoryDescription ?? r.category,
            categorySortOrder: ledgerCategory?.ledgerCategorySortOrder ?? 0,
            workerPositionSortOrder
        };
    });
}

export function getPostedLedger(fishingTripNumber: string, ignoreLoading: boolean = false) {
    return async (dispatch, getState) => {
        if (!ignoreLoading) {
            dispatch(ledgerSlice.actions.startLoading());
        }
        try {
            const client = new LedgerClient(undefined, axios);
            const response = await client.getPostedLedger(fishingTripNumber);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(ledgerSlice.actions.getPostedLedgerSuccess(payload));
        } catch (error) {
            dispatch(ledgerSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export async function getPayGlobalExportData(exportId: string) {
    try {
        const client = new LedgerClient(undefined, axios);
        return await client.getPayGlobalExportData(exportId);
    } catch (error) {
        handleError(error);
        return [];
    }
}